import { useState, useCallback } from "react";
import { useNavigate, useLoaderData } from "react-router-dom";
import { createPost, updatePost, getUploadUrl } from "../../../api/cms";
import RichTextEditor from "../../../components/RichTextEditor";
import { resizeImage } from "../../../utils/resize-image";

const inputClass = "border border-gray-300 rounded px-3 py-2 font-normal focus:outline-none focus:ring-2 focus:ring-bahyellow";
const Field = ({ label, children }) => (
  <label className="flex flex-col gap-1 text-sm font-medium">{label}{children}</label>
);

const toDateInputValue = (date) => {
  if (!date) return "";
  const d = new Date(date);
  if (isNaN(d)) return "";
  return d.toISOString().split("T")[0];
};

export default function AdminPostForm() {
  const existing = useLoaderData();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    title: "",
    content: "",
    authors: [],
    ...existing,
    date: toDateInputValue(existing?.date),
    tags: (existing?.tags || []).join(", "),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleImageUpload = useCallback(async (file) => {
    const { blob, contentType } = await resizeImage(file, { maxWidth: 1200, maxHeight: 1200 });
    const ext = contentType === 'image/png' ? 'png' : 'jpg';
    const key = `img/news/${crypto.randomUUID()}.${ext}`;
    const { url } = await getUploadUrl(key, contentType);
    const res = await fetch(url, { method: 'PUT', body: blob, headers: { 'Content-Type': contentType } });
    if (!res.ok) throw new Error(`S3 error ${res.status}`);
    return `https://brusselsafricahub.eu/${key}`;
  }, []);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const setAuthor = (i, field, value) =>
    setForm((f) => {
      const authors = [...f.authors];
      authors[i] = { ...authors[i], [field]: value };
      return { ...f, authors };
    });
  const addAuthor = () => setForm((f) => ({ ...f, authors: [...f.authors, { name: "", link: "" }] }));
  const removeAuthor = (i) => setForm((f) => ({ ...f, authors: f.authors.filter((_, idx) => idx !== i) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...form,
        date: form.date ? new Date(form.date).toISOString() : null,
        tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : [],
        authors: form.authors.filter((a) => a.name),
      };
      if (existing?.id) {
        await updatePost(existing.id, payload);
      } else {
        await createPost(payload);
      }
      navigate("/admin/posts");
    } catch {
      setError("Failed to save. Check your API key and try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-5xl">
      <h1 className="text-2xl font-bold mb-6">{existing?.id ? "Edit post" : "Add post"}</h1>
      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 flex flex-col gap-5">
        <Field label="Title">
          <input className={inputClass} value={form.title} onChange={set("title")} required />
        </Field>
        <Field label="Date">
          <input className={inputClass} type="date" value={form.date} onChange={set("date")} required />
        </Field>
        <Field label="Tags (comma-separated)">
          <input className={inputClass} value={form.tags} onChange={set("tags")} placeholder="e.g. finance, africa" />
        </Field>

        <div className="flex flex-col gap-1 text-sm font-medium">
          Authors
          {form.authors.map((author, i) => (
            <div key={i} className="flex gap-2">
              <input
                className={`${inputClass} flex-1`}
                value={author.name}
                onChange={(e) => setAuthor(i, "name", e.target.value)}
                placeholder="Name"
              />
              <input
                className={`${inputClass} flex-1`}
                value={author.link}
                onChange={(e) => setAuthor(i, "link", e.target.value)}
                placeholder="LinkedIn URL"
              />
              <button type="button" onClick={() => removeAuthor(i)} className="text-red-400 hover:text-red-600 px-2">✕</button>
            </div>
          ))}
          <button type="button" onClick={addAuthor} className="self-start text-blue-600 hover:underline text-xs mt-1">
            + Add author
          </button>
        </div>

        <div className="flex flex-col gap-1 text-sm font-medium">
          Content
          <RichTextEditor
            value={form.content}
            onChange={(val) => setForm((f) => ({ ...f, content: val }))}
            onUploadImage={handleImageUpload}
            onUploadError={(err) => setError(`Image upload failed: ${err.message}`)}
          />
        </div>

        {error && <p className="text-red-500 text-sm">{error}</p>}
        <div className="flex gap-3 mt-2">
          <button type="submit" disabled={saving} className="bg-bahyellow rounded px-4 py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-50">
            {saving ? "Saving…" : "Save"}
          </button>
          <button type="button" onClick={() => navigate('/admin/posts')} className="rounded px-4 py-2 text-sm border border-gray-300 hover:bg-gray-50">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
